import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { assert } from '../../../../../core/assert/assert';
import { TimeBlockProjectModel } from '../../../../../core/models/timeblock/time-block.model';
import { SharedClockHandlerService } from '../../../../../core/services/features/time-tracking/shared-clock-handler.service';
import { SharedTimerHandlerService } from '../../../../../core/services/features/time-tracking/shared-timer-handler.service';
import { DragOrResizeInteraction } from '../../../../../shared/data-types/interact.types';
import { CalendarViews } from '../../../calendar-types';
import { CalendarService } from '../../../services/calendar.service';
import { DateTimeHelper } from '../../../util/date-time-helper';
import { TimeBlockDurationCalculationService } from '../calculation/duration/time-block-duration-calculation.service';
import { ParallelTimeBlockGeometryCalculatorService } from '../calculation/geometry/parallel-time-block-geometry-calculator.service';
import { TimeBlockGeometryService } from '../calculation/geometry/time-block-geometry.service';
import { TimeBlockCrudService } from '../crud/time-block-crud.service';
import { TimeBlockItemBuilderService } from '../generation/time-block-item-builder.service';
import { TimeBlockDragResizeControllerService } from '../interaction/movement/drag-and-resize/time-block-drag-resize-handling/time-block-drag-resize-controller.service';
import { TimeBlockService } from '../services/time-block.service';
import { TimeBlockStructureService } from '../time-block-structure/time-block-structure.service';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { TimeBlockType } from '../types/time-block-types';

@Injectable()
export class SharedActiveTimeBlockService {
  public static noActiveTimerTimeBlockId = -1;

  public readonly timerActiveTBSubject = new BehaviorSubject<number | null>(null);

  constructor(
    private readonly timeBlockGeometryService: TimeBlockGeometryService,
    private readonly timeBlockCrudService: TimeBlockCrudService,
    private readonly parallelTimeBlockGeometryCalculatorService: ParallelTimeBlockGeometryCalculatorService,
    private readonly calendarService: CalendarService,
    private readonly timeBlockService: TimeBlockService,
    private readonly timeBlockDragResizeControllerService: TimeBlockDragResizeControllerService,
    private readonly timeBlockItemBuilderService: TimeBlockItemBuilderService,
    private readonly timeBlockStructureService: TimeBlockStructureService,
    private readonly timeBlockDurationCalculationService: TimeBlockDurationCalculationService,
    private readonly sharedClockHandlerService: SharedClockHandlerService,
    private readonly sharedTimerHandlerService: SharedTimerHandlerService,
  ) {
    this.sharedTimerHandlerService.timerActiveTBChanged$.subscribe((id) => {
      this.timerActiveTBSubject.next(id);
    });
  }

  public insertActiveTimeBlock(generatedTimeBlock: ITimeBlockComponentItem): number {
    // Only render active time block if it is inside of calendar's visible range.
    if (!this.timeBlockService.isTimeBlockOutsideOfView(generatedTimeBlock, true)) {
      this.timeBlockCrudService.insertTimeBlock(generatedTimeBlock);
      this.parallelTimeBlockGeometryCalculatorService.updateParallelTimeBlockGeometries(false);
      this.timeBlockDurationCalculationService.calculateTimeBlockDurationSums$.next(null);
    }
    return generatedTimeBlock.id;
  }

  public replaceActiveTimeBlock(activeTimeBlockId: number): number {
    const activeTimeBlock = this.timeBlockStructureService.retrieveTimeBlockPart(
      activeTimeBlockId,
    ) as ITimeBlockComponentItem;
    const clone = activeTimeBlock.clone(this.timeBlockItemBuilderService);
    clone.timeBlockModel.isActive = false;
    this.replace(clone, true);
    return SharedActiveTimeBlockService.noActiveTimerTimeBlockId;
  }

  public replace(activeClone: ITimeBlockComponentItem, allTimeBlockParts: boolean): void {
    // Replace all time block parts (i.e. delete old time block parts, create new time block parts)
    if (allTimeBlockParts) {
      this.timeBlockCrudService.removeTimeBlock(activeClone);
      this.timeBlockCrudService.insertTimeBlock(activeClone);
    } else {
      // Only update the extending active time block part
      this.timeBlockGeometryService.calculateInitialTimeBlockGeometry(activeClone);
      this.timeBlockCrudService.replaceTimeBlock(activeClone);
    }
    this.parallelTimeBlockGeometryCalculatorService.updateParallelTimeBlockGeometries(false);
    this.timeBlockDurationCalculationService.calculateTimeBlockDurationSums$.next(null);
  }

  public canUpdate(activeTimeBlockId: number) {
    return (
      this.calendarService.model.calendarViewMode !== CalendarViews.MonthGrid &&
      typeof activeTimeBlockId === 'number' &&
      activeTimeBlockId >= 0 &&
      this.timeBlockDragResizeControllerService.currentDragResizeInteraction ===
        DragOrResizeInteraction.None
    );
  }

  public updateActiveTimeBlock(
    activeTimeBlockId: number,
    activeTrackerTimeBlock?: ITimeBlockComponentItem,
  ): void {
    assert(activeTimeBlockId !== null, 'Active time block not set.');

    // Retrieve active time block
    const activeTimeBlockParts = this.timeBlockStructureService.retrieveTimeBlockPart(
      activeTimeBlockId,
      TimeBlockType.ExistingBlock,
      -1,
    ) as ITimeBlockComponentItem[];

    // If the insertion process of the active time block has not finished yet, return.
    if (!activeTimeBlockParts) {
      return;
    }

    // Select the last part of the active time block compound.
    const tail = activeTimeBlockParts[activeTimeBlockParts.length - 1];
    const currentEnd = new Date(tail.timeBlockModel.end);

    const clone = tail.clone(this.timeBlockItemBuilderService);

    const elapsedSeconds = this.sharedClockHandlerService.getCurrentTimerSeconds();
    clone.timeBlockModel.end = this.getActiveTimeBlockEnd(clone, elapsedSeconds);

    // We only replace the time block if it really needs to be rendered (i.e. when a new minute starts).
    if (currentEnd.getMinutes() !== clone.timeBlockModel.end.getMinutes()) {
      this.replace(clone, true);
    }

    if (activeTrackerTimeBlock) {
      const timeBlockModel = clone.timeBlockModel as TimeBlockProjectModel;
      const currentTimeBlockModel = activeTrackerTimeBlock.timeBlockModel as TimeBlockProjectModel;

      timeBlockModel.project = currentTimeBlockModel.project;
      timeBlockModel.task = currentTimeBlockModel.task;
      timeBlockModel.description = currentTimeBlockModel.description;
      // Update time block in calendar model.
      this.timeBlockCrudService.replaceTimeBlock(clone);
    }
  }

  private getActiveTimeBlockEnd(
    activeTimeBlock: ITimeBlockComponentItem,
    elapsedSeconds: number,
  ): Date {
    assert(activeTimeBlock !== null, 'Active time block array not set');

    const minimumTimeBlockDuration =
      this.calendarService.model.calendarProperties.minimumTimeBlockDuration;

    const start = DateTimeHelper.setSecondsAndMillisToZero(activeTimeBlock.timeBlockModel.start);
    const diff = DateTimeHelper.minutesToSeconds(minimumTimeBlockDuration) - elapsedSeconds;
    // After the time block exceeds the minimum time block duration, we use the start time and add the elapsed seconds.
    if (diff < 0) {
      return DateTimeHelper.addSeconds(start, elapsedSeconds);
    }

    // If we are within the minimum time block duration range, use the end time.
    // Zero the seconds here so that in the time block dialog, end time is consistent.
    return DateTimeHelper.setSecondsAndMillisToZero(activeTimeBlock.timeBlockModel.end);
  }
}
